import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};
  align-self: flex-start;

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const TableWrapper = styled.div`
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 12px;

  thead th {
    ${adminTableHeaderCell};
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px 10px;
  }
`;

const LinkButton = styled.a`
  display: inline-block;
  padding: 4px 8px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 20px;
  border-radius: 5px;
`;

const Field = styled.div`
  margin-bottom: 1em;

  label {
    display: block;
    margin-bottom: 0.3em;
    color: ${({ colors }) => colors.labelText};
    font-size: 12px;
  }

  input[type="text"],
  input[type="email"] {
    width: 100%;
    padding: 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 12px;
  }

  input[type="checkbox"] {
    margin-right: 0.5em;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1.5em;

  input[type="submit"] {
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }

  a.cancel {
    margin-left: 1em;
    color: ${({ colors }) => colors.labelText};
    text-decoration: none;
    cursor: pointer;
    font-size: 12px;
    &:hover {
      color: ${({ colors }) => colors.buttonBackground};
    }
  }
`;

export default function SalesPersonContent({ colors, onTabChange }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  // view: 'list' | 'edit' | 'create'
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);

  // form state (edit)
  const [email, setEmail] = useState("");
  const [active, setActive] = useState(false);

  // form state (create)
  const [newCode, setNewCode] = useState("");
  const [newEmail, setNewEmail] = useState("");

  // modals
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [pendingAction, setPendingAction] = useState(null);

  // load and group salespersons
  const loadSales = async () => {
    setLoading(true);
    try {
      const resp = await API.getSalesPerson({});
      const rows = resp.result || [];
      const map = {};
      rows.forEach((r) => {
        const code = r.sp_code;
        if (!map[code]) {
          map[code] = {
            sp_code: code,
            is_active: r.is_active,
            user_id: r.user_id,
            first_name: r.first_name,
            last_name: r.last_name,
            states: [],
          };
        }
        if (r.state_name) map[code].states.push(r.state_name);
      });
      setSales(Object.values(map));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  // handle Edit click
  const handleEditClick = (s) => {
    setSelected(s);
    setEmail("");
    setActive(s.is_active === "1");
    setView("edit");
  };

  // handle Create click
  const handleCreateClick = () => {
    setNewCode("");
    setNewEmail("");
    setView("create");
  };

  // common: show result modal
  const showResult = (title, msg, status = "Success") => {
    const normalizedStatus = status === "Failed" ? "Failed" : "Success";
    setModalTitle(title);
    setModalMessage(msg);
    setModalStatus(normalizedStatus);
    setResultOpen(true);
  };

  // confirm / result modal flow
  const onModalConfirm = async () => {
    setConfirmOpen(false);
    if (pendingAction === "update") {
      try {
        const resp = await API.updateSalesPerson({
          email,
          sp_code: selected.sp_code,
          is_active: active ? 1 : 0,
          user_id: selected.user_id,
        });
        await loadSales();
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        showResult(
          status === "Success" ? "Success" : "Error",
          resp?.message || "Salesperson updated.",
          status
        );
      } catch (err) {
        console.error(err);
        showResult("Error", "Update failed.", "Failed");
      } finally {
        setView("list");
      }
    } else if (pendingAction === "create") {
      try {
        const resp = await API.addSalesPerson({
          email: newEmail,
          sp_code: newCode,
        });
        await loadSales();
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        showResult(
          status === "Success" ? "Success" : "Error",
          resp?.message || "Salesperson added.",
          status
        );
      } catch (err) {
        console.error(err);
        showResult("Error", "Create failed.", "Failed");
      } finally {
        setView("list");
      }
    }
    setPendingAction(null);
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  // handle edit submit
  const handleEditSubmit = (e) => {
    e.preventDefault();
    setModalTitle("Confirm Update");
    setModalMessage("Save changes to this salesperson?");
    setPendingAction("update");
    setConfirmOpen(true);
  };

  // handle create submit
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    setModalTitle("Confirm Add");
    setModalMessage("Add this new salesperson?");
    setPendingAction("create");
    setConfirmOpen(true);
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Container>
      {/* confirmation */}
      <ConfirmationModal
        show={confirmOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onModalConfirm}
      />
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {view === "list" ? (
                <>
                  <li>
                    <a onClick={handleCreateClick}>Add New</a>
                  </li>
                  <li>
                    <a
                      onClick={() => {
                        if (onTabChange) onTabChange("Sales Regions");
                      }}
                    >
                      View Sale Regions
                    </a>
                  </li>
                </>
              ) : (
                <li>
                  <a onClick={() => setView("list")}>Back to List</a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <View>
        {view === "list" && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>States</th>
                  <th>Active</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {sales?.map((s) => (
                  <tr key={s.sp_code}>
                    <td>{s.sp_code}</td>
                    <td>
                      {s.first_name || s.last_name
                        ? `${s.first_name || ""} ${s.last_name || ""}`.trim()
                        : "Not yet signed up"}
                    </td>
                    <td>{s.states.join(",")}</td>
                    <td>
                      <span style={{ color: s.is_active === "1" ? "green" : "red" }}>
                        {s.is_active === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      {s.user_id ? (
                        <LinkButton colors={colors} onClick={() => handleEditClick(s)}>
                          Edit
                        </LinkButton>
                      ) : (
                        <small></small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}

        {view === "edit" && selected && (
          <>
            <h1 style={{ color: colors.labelText }}>Editing: {selected.sp_code}</h1>
            <Form colors={colors} onSubmit={handleEditSubmit}>
              <Field colors={colors}>
                <label>E-mail Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field colors={colors}>
                <label>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  Active
                </label>
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit Changes" />
                <a className="cancel" onClick={() => setView("list")}>
                  Cancel Edit
                </a>
              </SubmitRow>
            </Form>
          </>
        )}

        {view === "create" && (
          <>
            <h1 style={{ color: colors.labelText }}>Adding A New Salesperson</h1>
            <Form colors={colors} onSubmit={handleCreateSubmit}>
              <Field colors={colors}>
                <label>
                  Enter initials. For example: “John Smith” → “js”
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                />
              </Field>
              <Field colors={colors}>
                <label>Enter E-mail address</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
                <a className="cancel" onClick={() => setView("list")}>
                  Cancel
                </a>
              </SubmitRow>
            </Form>
          </>
        )}
      </View>

      <div style={{ clear: "both" }} />
    </Container>
  );
}
